/**
 * Shared content for product accelerators. Used by /products (catalog) and
 * /products/:slug (detail) pages.
 */

export const PRODUCTS = [
    {
        slug: "talentiqx",
        name: "TalentIQX",
        maturity: "Live / Built",
        whoFor: "Staffing teams, recruiters, delivery heads",
        firstUseCase: "Shortlist better candidates faster",
        tagline: "AI-powered talent intelligence platform",
        short: "AI-powered matching, resume scoring and skill mapping for hiring teams.",
        problem:
            "Recruiters and hiring teams spend most of their time on resume triage instead of high-value conversations with candidates and hiring managers.",
        capabilities: [
            "Resume parsing and skill graph extraction at scale",
            "Role-aware candidate scoring and ranking",
            "Recruiter workspace with full pipeline visibility",
            "Explainable match reasoning for audits and stakeholder buy-in",
            "Integrations with ATS, VMS and existing Excel-based workflows",
            "Bench visibility, talent pools and re-engagement workflows",
        ],
        users: ["Talent acquisition leaders", "Staffing firms", "Recruitment operations"],
        outcomes: [
            { value: "48h", label: "Avg time-to-shortlist" },
            { value: "3.5×", label: "Recruiter productivity" },
            { value: "97%", label: "Parse accuracy" },
        ],
        story: {
            title: "Mid-market staffing firm — 3.5× recruiter productivity",
            copy: "A mid-market IT staffing firm was processing 2,000+ inbound resumes per week with a 6-person recruiter team. After deploying TalentIQX with role-aware scoring and recruiter workspaces, time-to-shortlist dropped from 11 days to 48 hours and recruiter productivity rose 3.5× — without expanding headcount.",
        },
        moduleHighlights: [
            { title: "Skill graph", copy: "Auto-built from your historical placements and live job requirements." },
            { title: "Explainability", copy: "Every score backed by reasoning a recruiter can use in conversations." },
            { title: "Workflow fit", copy: "Drop into ATS/VMS-driven processes — no rip and replace." },
        ],
    },
    {
        slug: "recruitiqx",
        name: "RecruitIQX",
        maturity: "Pilot-ready",
        whoFor: "Recruiting operations, delivery managers, VMS administrators",
        firstUseCase: "Streamline submission tracking and vendor coordination",
        tagline: "AI-assisted recruitment operations platform",
        short: "Requirements, submissions, interviews, vendor coordination and hiring analytics in one place.",
        problem:
            "Recruitment data is fragmented across requirements, submissions, vendor partners, interviews and reporting — making it hard to run the function as a measurable operation.",
        capabilities: [
            "Requirements management with priority signals and SLAs",
            "Submission and interview tracking with structured feedback",
            "Vendor coordination, SLAs and partner scorecards",
            "Hiring analytics with funnel reporting and forecasts",
            "Workflow automation for repetitive recruiter tasks",
            "Pluggable AI matching via TalentIQX integration",
        ],
        users: ["Recruiting operations", "Delivery managers", "VMS administrators"],
        outcomes: [
            { value: "1×", label: "Source of truth" },
            { value: "−42%", label: "Coordination time" },
            { value: "100%", label: "Vendor SLA visibility" },
        ],
        story: {
            title: "VMS partner — coordination time down 42%",
            copy: "A vendor-management partner moved their entire recruitment operation onto RecruitIQX, replacing five spreadsheets and three email threads per requirement. Coordination time dropped 42% and vendor SLA visibility moved from periodic to continuous.",
        },
        moduleHighlights: [
            { title: "Operating dashboard", copy: "Real-time view of pipelines, vendors, interviews and forecasts." },
            { title: "Vendor scorecards", copy: "Performance signals make conversations with partners objective." },
            { title: "Automation", copy: "Repetitive coordination work handled by built-in playbooks." },
        ],
    },
    {
        slug: "docuiqx",
        name: "DocuIQX",
        maturity: "Custom accelerator",
        whoFor: "Operations teams, finance & legal, compliance officers",
        firstUseCase: "Automate document intake and structured data extraction",
        tagline: "Enterprise document intelligence platform",
        short: "Extract, classify, summarise and route business documents with confidence.",
        problem:
            "Critical business documents are unstructured, scattered across systems and slow to act on — creating exception queues, customer delays and audit headaches.",
        capabilities: [
            "Extraction across PDFs, scans, emails and structured forms",
            "Classification, summarisation and rule-based routing",
            "Human-in-the-loop review and approval flows",
            "Auditable processing with confidence scoring and trails",
            "Connectors for SharePoint, S3, Google Drive, ERPs and ITSMs",
            "Domain templates for finance, legal, claims and supply documents",
        ],
        users: ["Operations teams", "Finance & legal", "Compliance officers"],
        outcomes: [
            { value: "5×", label: "Doc intake throughput" },
            { value: "−65%", label: "Manual entry" },
            { value: "Audit-ready", label: "Trails" },
        ],
        story: {
            title: "Insurance carrier — claims intake cycle down 65%",
            copy: "An insurance carrier processing claims across email, fax and partner uploads deployed DocuIQX with claims-specific templates. Intake cycle time fell 65%. Adjusters refocused on judgement work instead of typing.",
        },
        moduleHighlights: [
            { title: "Confidence-first", copy: "Every field carries a confidence score and review path." },
            { title: "Template library", copy: "Industry templates ship with the product — extend as you go." },
            { title: "Compliance lens", copy: "Every action is logged, traceable and exportable for audit." },
        ],
    },
    {
        slug: "agentiqx",
        name: "AgentIQX",
        maturity: "Custom accelerator",
        whoFor: "Operations leaders, customer support, internal platform teams",
        firstUseCase: "Deploy policy-governed custom agents for internal operations",
        tagline: "Custom AI agents for the enterprise",
        short: "Custom AI agents for HR, support, finance, compliance and internal operations.",
        problem:
            "Internal teams don't need another chatbot — they need AI that acts on systems, follows policies and produces work product the business can rely on.",
        capabilities: [
            "Domain-specific agents for HR, support, finance and IT",
            "Tool-use across CRMs, ITSM, ERPs and internal APIs",
            "Policy-aware guardrails and granular access controls",
            "Conversation, dashboard and email surfaces — your choice",
            "Evaluation, observability and continual improvement",
            "Human approval gates baked into every high-stakes path",
        ],
        users: ["Operations leaders", "Customer support", "Internal platform teams"],
        outcomes: [
            { value: "Tool-using", label: "Not chat-only" },
            { value: "Audited", label: "By default" },
            { value: "Multi-surface", label: "Chat · Email · UI" },
        ],
        story: {
            title: "Global SaaS — HR ops agent saving 1,200 hrs / month",
            copy: "A global SaaS company deployed AgentIQX agents inside HR ops to handle policy lookups, document drafting and ticket routing. The team reclaimed 1,200+ hours per month, with policy-aware guardrails preventing every out-of-policy response.",
        },
        moduleHighlights: [
            { title: "Agents that act", copy: "Tool-use across the systems you already pay for." },
            { title: "Policy-aware", copy: "Guardrails defined by your team — enforced at every step." },
            { title: "Observability", copy: "Every conversation, tool call and outcome is traceable." },
        ],
    },
];

export const getProduct = (slug) => PRODUCTS.find((p) => p.slug === slug);
